import React, { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { useToast } from '@/hooks/use-toast';
import { supabase } from '@/integrations/supabase/client';
import { Key } from 'lucide-react';

interface FixEmailDialogProps {
  author: {
    id: string;
    name: string;
    email: string;
  };
}

const FixEmailDialog: React.FC<FixEmailDialogProps> = ({ author }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [newEmail, setNewEmail] = useState(author.email);
  const [isLoading, setIsLoading] = useState(false);
  const { toast } = useToast();

  const handleFixEmail = async () => {
    if (!newEmail.trim()) {
      toast({
        title: 'Error',
        description: 'Please enter a valid email address',
        variant: 'destructive',
      });
      return;
    }

    setIsLoading(true);
    try {
      const { data, error } = await supabase.functions.invoke('update-user-email', {
        body: {
          userId: author.id,
          newEmail: newEmail.trim(),
        },
      });

      if (error) throw error;

      toast({
        title: 'Success',
        description: `Email updated successfully. ${author.name} can now login with ${newEmail}`,
      });
      
      setIsOpen(false);
    } catch (error: any) {
      console.error('Error updating email:', error);
      toast({
        title: 'Failed to update email',
        description: error.message || 'An unexpected error occurred',
        variant: 'destructive',
      });
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={setIsOpen}>
      <DialogTrigger asChild>
        <Button variant="ghost" size="sm">
          <Key className="h-4 w-4 mr-1" />
          Fix Auth
        </Button>
      </DialogTrigger>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Fix Authentication Email</DialogTitle>
        </DialogHeader>
        
        <div className="space-y-4">
          <p className="text-sm text-muted-foreground">
            This will update the email address in both the authentication system and user profile for {author.name}.
          </p>
          
          <div className="space-y-2">
            <Label htmlFor="email">New Email Address</Label>
            <Input
              id="email"
              type="email"
              value={newEmail}
              onChange={(e) => setNewEmail(e.target.value)}
              placeholder="Enter new email address"
            />
          </div>
          
          <div className="flex justify-end gap-2">
            <Button variant="outline" onClick={() => setIsOpen(false)}>
              Cancel
            </Button>
            <Button onClick={handleFixEmail} disabled={isLoading}>
              {isLoading ? 'Updating...' : 'Update Email'}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default FixEmailDialog;